import { LOCALIZED_PATHS, BASE_URL, DEV_URL } from '../constants/url-constants';

export type Locale = keyof typeof LOCALIZED_PATHS;

export interface JobUrlParams {
  baseUrl: string;
  jobId: string;
  locale: Locale;
  title?: string;
  company?: string;
  location?: string;
  remoteness?: string;
}

export interface SearchUrlParams {
  locale: Locale;
  query?: string;
  page?: number;
  refinementList?: {
    location?: string[];
    companyIndustry?: string[];
    companyId?: string[];
    requiredLanguages?: string[];
    [key: string]: string[] | undefined;
  };
}

export interface BlogUrlParams {
  locale: Locale;
  slug: string;
}

export interface ProfileUrlParams {
  locale: Locale;
  section: 'user-profile-overview' | 'saved-jobs' | 'saved-searches';
}

/**
 * Validates if a value is a non-empty string
 */
function isValidString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Sanitizes a string for use in URLs by removing special characters and replacing spaces with hyphens
 * Returns empty string if input is not a valid string
 */
export function sanitizeForUrl(str: unknown): string {
  if (!isValidString(str)) {
    return '';
  }

  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
}

/**
 * Generates a SEO-friendly URL for a job posting
 * The location parameter should only be the first city (not a comma-joined list)
 */
export function generateJobUrl({ baseUrl, jobId, locale, company, title, location, remoteness }: JobUrlParams): string {
  if (!isValidString(jobId)) {
    throw new Error('Job ID must be a non-empty string');
  }
  // Use the original company name as the companyId path segment (not sanitized)
  let url = `${baseUrl}/${locale}/${LOCALIZED_PATHS[locale].job}/${jobId}`;
  url += `/${company || ''}`;
  // Add SEO-friendly elements if available and valid
  const seoElements: string[] = [];
  const sanitizedTitle = sanitizeForUrl(title);
  const sanitizedLocation = sanitizeForUrl(location);
  const sanitizedRemoteness = sanitizeForUrl(remoteness);
  if (sanitizedTitle) seoElements.push(sanitizedTitle);
  if (sanitizedLocation) seoElements.push(sanitizedLocation);
  if (sanitizedRemoteness) seoElements.push(sanitizedRemoteness);
  if (seoElements.length > 0) {
    url += '/' + seoElements.join('-');
  }
  return url;
}

/**
 * Parses a job URL to extract parameters
 */
export function parseJobUrl(url: string): JobUrlParams | null {
  // Match /{locale}/{jobPath}/{jobId}/{companyId}/{seoSlug}
  const localePaths = (Object.entries(LOCALIZED_PATHS) as [Locale, (typeof LOCALIZED_PATHS)[Locale]][])
    .map(([locale, paths]) => `${locale}/${paths.job}`)
    .join('|');
  const regex = new RegExp(`${BASE_URL}/(${localePaths})/([^/]+)/([^/]+)(?:/(.+))?`);
  const match = url.match(regex);
  if (!match) return null;
  const [, localePath, jobId, companyId] = match;
  const locale = localePath.split('/')[0] as Locale;
  if (!isLocale(locale)) return null;
  return { locale, jobId, company: companyId, baseUrl: '' };
}

/**
 * Type guard for Locale
 */
export function isLocale(str: string): str is Locale {
  return str in LOCALIZED_PATHS;
}

/**
 * Generates a URL for search results
 */
export function generateSearchUrl(params: SearchUrlParams): string {
  const { locale, query = '', page = 1, refinementList = {} } = params;

  // Convert refinementList to URL parameters
  const refinements = Object.entries(refinementList)
    .filter(([, value]) => Array.isArray(value) && value.length > 0)
    .map(([key, value]) => {
      // Ensure all values in the array are strings
      const validValues = value!.filter(isValidString);
      if (validValues.length === 0) return '';
      return `${encodeURIComponent(key)}=${encodeURIComponent(validValues.join(','))}`;
    })
    .filter(Boolean) // Remove empty strings
    .join('&');

  let url = `${BASE_URL}/${locale}/${LOCALIZED_PATHS[locale].search}`;
  const queryParams: string[] = [];

  // Only add query if it's a valid string
  const sanitizedQuery = isValidString(query) ? query : '';
  if (sanitizedQuery) queryParams.push(`q=${encodeURIComponent(sanitizedQuery)}`);

  // Validate page number
  const validPage = typeof page === 'number' && page > 1 ? page : null;
  if (validPage) queryParams.push(`page=${validPage}`);

  if (refinements) queryParams.push(refinements);

  if (queryParams.length > 0) {
    url += '?' + queryParams.join('&');
  }

  return url;
}

/**
 * Generates a URL for a resources/blog post
 */
export function generateBlogUrl({ locale, slug }: BlogUrlParams): string {
  if (!isValidString(slug)) {
    throw new Error('Resources slug must be a non-empty string');
  }

  return `${BASE_URL}/${locale}/${LOCALIZED_PATHS[locale].blog}/${sanitizeForUrl(slug)}`;
}

/**
 * Generates a URL for a profile section
 */
export function generateProfileUrl({ locale, section }: ProfileUrlParams): string {
  const sectionPath =
    section === 'saved-jobs'
      ? LOCALIZED_PATHS[locale].savedJobs
      : section === 'saved-searches'
      ? LOCALIZED_PATHS[locale].savedSearches
      : LOCALIZED_PATHS[locale].profile;
  return `${BASE_URL}/${locale}/${sectionPath}`;
}

/**
 * Extracts locale from a URL
 */
export function extractLocaleFromUrl(url: string): Locale | null {
  if (!isValidString(url)) {
    return null;
  }

  const match = url.match(new RegExp(`${BASE_URL}/([^/]+)`));
  if (!match) return null;

  const [, locale] = match;
  return isLocale(locale) ? locale : null;
}

/**
 * Validates if a URL belongs to our domain
 */
export function isInternalUrl(url: string): boolean {
  return isValidString(url) && url.startsWith(BASE_URL);
}

/**
 * Converts an absolute URL to a relative path
 */
export function toRelativePath(url: string): string {
  if (!isValidString(url)) {
    return '';
  }
  // Remove both BASE_URL and DEV_URL to handle both production and development
  return url.replace(BASE_URL, '').replace(DEV_URL, '');
}
